<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';

import type {
  AutoCompleteSuggestion,
  AutoCompleteSuggestionsByCategory,
  CategoryType,
} from '@libs/trip-planner-interfaces';

import AppInput from '../appInput/AppInput.vue';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import AppAutosuggestDropdown from '../appAutosuggestDropdown/AppAutosuggestDropdown.vue';
import type { AppInputTheme } from '../appInput/AppInput.model';
import type { DropdownOpeningDirection } from '../selects/models/common.models';
import { KeyboardKeyCode } from '../../models';

export interface AppInputAutosuggestDropdownProps {
  suggestions?: AutoCompleteSuggestionsByCategory[];
  filterByCategoryType?: CategoryType[];
  inputText: string;
  placeholder?: string;
  theme?: AppInputTheme;
  maxVisibleItems?: number;
  shouldDisplayMobileVersion?: boolean;
  maxLength?: number;
  disable?: boolean;
  highlight?: boolean;
  shouldFocusOnInit?: boolean;
  headerIcon?: boolean;
  wrapperBottomBoundary?: number;
}

const props = withDefaults(defineProps<AppInputAutosuggestDropdownProps>(), {
  suggestions: () => [],
  placeholder: '',
  theme: 'dark',
  maxVisibleItems: 0,
  shouldDisplayMobileVersion: false,
  maxLength: undefined,
  closeIcon: false,
  disable: false,
  highlight: false,
  headerIcon: false,
});

interface AppInputAutosuggestDropdownEmits {
  (eventName: 'update-model-value', value: string): void;
  (eventName: 'select', item: AutoCompleteSuggestion): void;
  (eventName: 'close'): void;
  (eventName: 'on-input-focus', value: boolean): void;
  (eventName: 'on-input-click'): void;
}
const emit = defineEmits<AppInputAutosuggestDropdownEmits>();

const isInputFocused = ref(false);
const dropdownRef = ref();
const appInputRef = ref();
const shouldOpenAbove = ref(false);
const shouldOpenDropdown = computed(
  () => isInputFocused.value && !!filteredItems.value.length
);
const userInputValue = ref<string | undefined>(undefined);

const inputValue = computed(() =>
  userInputValue.value !== undefined ? userInputValue.value : props.inputText
);

const filteredItems = computed(() => {
  const query = inputValue.value.toLowerCase().trim();
  if (!query) {
    return props.suggestions;
  }
  return props.suggestions
    .map((category) => {
      if (
        props.filterByCategoryType &&
        !props.filterByCategoryType.includes(category.type)
      ) {
        return null;
      }
      const filteredData = category.data.filter((item) =>
        item.label.toLowerCase().includes(query)
      );

      return filteredData.length > 0
        ? { ...category, data: filteredData }
        : null;
    })
    .filter(Boolean) as AutoCompleteSuggestionsByCategory[];
});

const openingDirection = computed<DropdownOpeningDirection | undefined>(() => {
  if (shouldOpenDropdown.value) {
    if (shouldOpenAbove.value) {
      return 'open-above';
    }
    return 'open-below';
  }
  return undefined;
});

function updateShouldOpenAbove(value: boolean): void {
  shouldOpenAbove.value = value;
}

function handleTextChange(value: string): void {
  userInputValue.value = value;
  emit('update-model-value', value);
}

function handleFocusChange(isFocused: boolean): void {
  if (isFocused && !isInputFocused.value) {
    isInputFocused.value = isFocused;
    nextTick(() => {
      const inputElement = appInputRef.value?.$el.querySelector('input');
      if (inputElement) {
        inputElement.focus();
        const length = inputElement.value.length;
        inputElement.setSelectionRange(length, length);
      }
    });
  } else if (isFocused) {
    isInputFocused.value = isFocused;
    nextTick(() => {
      appInputRef.value?.$el.querySelector('input')?.focus();
    });
  }
  emit('on-input-focus', isFocused);
}

function handleInputClick(event: Event): void {
  emit('on-input-click');
  if (!isInputFocused.value) {
    handleFocusChange(true);
  }
  event.stopPropagation();
}

function handleDropdownClose(): void {
  isInputFocused.value = false;

  emit('close');
}

function handleSelect(item: AutoCompleteSuggestion): void {
  emit('select', item);
  userInputValue.value = item.label;
  isInputFocused.value = false;

  nextTick(() => {
    emit('close');
    appInputRef.value?.$el.querySelector('input')?.blur();
  });
}

function closeIconClick(): void {
  userInputValue.value = '';

  nextTick(() => {
    appInputRef.value?.$el.querySelector('input')?.focus();
  });
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === KeyboardKeyCode.Enter && filteredItems.value.length > 0) {
    const firstItem = filteredItems.value[0]?.data[0];
    if (firstItem) {
      handleSelect(firstItem);
    }
  } else if (event.key === KeyboardKeyCode.Escape) {
    emit('close');
  }
}

onMounted(async () => {
  if (props.shouldFocusOnInit) {
    await nextTick(() => {
      appInputRef.value?.$el.querySelector('input')?.focus();
    });
  }
});

onClickOutside(dropdownRef, async () => {
  if (isInputFocused.value) {
    await isInputFocusedAction();
  }
});

async function isInputFocusedAction(): Promise<void> {
  userInputValue.value = props.inputText;
  isInputFocused.value = false;

  await nextTick(() => {
    emit('close');
    appInputRef.value?.$el.querySelector('input')?.blur();
  });
}

watch(
  () => isInputFocused.value,
  async (isFocused) => {
    if (!isFocused) {
      await isInputFocusedAction();
    }
  }
);
watch(
  () => props.inputText,
  (newValue) => {
    userInputValue.value = newValue;
  }
);
</script>

<template>
  <div class="app-input-autosuggest-dropdown">
    <AppAutosuggestDropdown
      ref="dropdownRef"
      :suggestions="filteredItems"
      :is-open="shouldOpenDropdown"
      :selected="inputValue"
      :max-visible-items="props.maxVisibleItems"
      :highlight="props.highlight"
      :wrapper-bottom-boundary="props.wrapperBottomBoundary"
      :header-icon="props.headerIcon"
      @select="handleSelect"
      @close="handleDropdownClose"
      @update-should-open-above="updateShouldOpenAbove"
    >
      <template #input>
        <AppInput
          ref="appInputRef"
          :input-text="inputValue"
          :placeholder="props.placeholder"
          :theme="props.theme"
          :should-display-mobile-version="props.shouldDisplayMobileVersion"
          :max-length="props.maxLength"
          :disable="props.disable"
          :opening-direction="openingDirection"
          @text-changed="handleTextChange"
          @focus-changed="handleFocusChange"
          @click="handleInputClick"
          @keydown="handleKeyDown"
        >
          <template #left-icon>
            <slot name="left-icon" />
          </template>
          <template v-if="inputValue && isInputFocused" #right-icon>
            <button
              type="button"
              class="close-icon"
              @click.stop="closeIconClick"
            >
              <AppSvgIcon name="close" size="16" />
            </button>
          </template>
        </AppInput>
      </template>
    </AppAutosuggestDropdown>
  </div>
</template>

<style scoped lang="scss">
@import '../../styles/_fonts.scss';
@import '../../styles/_media.scss';

.app-input-autosuggest-dropdown {
  position: relative;
  width: 100%;

  .close-icon {
    z-index: var(--modal-z-index);
    position: absolute;
    right: var(--spacing-16);
    top: 50%;
    transform: translateY(-50%);
    padding: var(--spacing-2);
    background-color: var(--color-soft-black);
    border-radius: 50%;
    color: var(--color-white);
  }
}
</style>
